import type { AxiosError, AxiosResponse } from "axios";
import api from "./api/api";

type ApiResponse<T> = {
  status: boolean;
  data: T;
  msg?: string;
};

function getErrorResponse<T>(error: unknown) {
  return (error as AxiosError<T>)?.response;
}

export type MesaMidiaPacingStatus =
  | "dentro_do_previsto"
  | "atencao"
  | "critico";

export type MesaMidiaBase = {
  proposta_item_key: string;
  proposta_item_label: string;
  entrega_label: string;
  entrega_plural_label: string;
  unidade_compra?: string | null;
  orcamento_total: number;
  data_inicial: string | null;
  data_final: string | null;
  impressoes_contratadas: number;
  meta_diaria_investimento: number;
};

export type MesaMidiaConfig = Partial<MesaMidiaBase> & {
  id: number;
  addesk_id: number;
  empresa_id: number;
  proposta_item_key: string;
};

export type MesaMidiaRegistro = {
  id: number;
  addesk_id: number;
  empresa_id: number;
  proposta_item_key: string;
  data: string;
  gasto_dia: number;
  impressoes_dia: number;
  cliques?: number | null;
  conversoes?: number | null;
  meta_diaria?: number | null;
  observacoes?: string | null;
};

export type MesaMidiaHistoricoItem = {
  id: number;
  data: string;
  gasto_dia: number;
  impressoes: number;
  cliques?: number | null;
  conversoes?: number | null;
  meta_diaria: number;
  cpm: number;
  percentual_entregue: number;
  percentual_investimento_consumido: number;
  status: MesaMidiaPacingStatus;
  status_label: string;
  observacoes?: string | null;
};

export type MesaMidiaGraficoGastoItem = {
  data: string;
  gasto_dia: number;
  meta_diaria: number;
};

export type MesaMidiaDashboard = {
  orcamento_total: number;
  gasto_acumulado: number;
  percentual_orcamento_consumido: number;
  residual_investimento: number;
  custo_medio_diario: number;
  impressoes_contratadas: number;
  impressoes_entregues: number;
  percentual_entrega: number;
  impressoes_restantes: number;
  dias_decorridos: number;
  dias_restantes: number;
  percentual_periodo_restante: number;
  percentual_periodo_decorrido: number;
  pacing_status: MesaMidiaPacingStatus;
  pacing_label: string;
  insight: string;
  grafico_gasto_diario: MesaMidiaGraficoGastoItem[];
  historico: MesaMidiaHistoricoItem[];
};

export type MesaMidiaPropostaItem = {
  key: string;
  label: string;
  resumo: string;
  orcamento_total: number;
  data_inicial: string | null;
  data_final: string | null;
  impressoes_contratadas: number;
  entrega_label: string;
  entrega_plural_label: string;
  unidade_compra?: string | null;
};

export type MesaMidiaData = {
  desk_id: number;
  proposta_item_key: string;
  item: MesaMidiaPropostaItem;
  itens: MesaMidiaPropostaItem[];
  base: MesaMidiaBase;
  config?: MesaMidiaConfig | null;
  registros: MesaMidiaRegistro[];
  dashboard: MesaMidiaDashboard;
};

export type MesaMidiaBasePayload = {
  proposta_item_key?: string;
  orcamento_total?: string | number | null;
  data_inicial?: string | null;
  data_final?: string | null;
  impressoes_contratadas?: string | number | null;
  meta_diaria_investimento?: string | number | null;
};

export type MesaMidiaDiarioPayload = {
  proposta_item_key?: string;
  data: string;
  gasto_dia: string | number;
  impressoes_dia: string | number;
  cliques?: string | number | null;
  conversoes?: string | number | null;
  meta_diaria?: string | number | null;
  observacoes?: string | null;
};

export const ServiceMesaMidiaAcompanhamento = {
  getResumo: async (
    deskId: number | string,
    propostaItemKey?: string,
  ): Promise<AxiosResponse<ApiResponse<MesaMidiaData>> | undefined> => {
    try {
      const params = propostaItemKey
        ? { proposta_item_key: propostaItemKey }
        : undefined;
      return await api.get<ApiResponse<MesaMidiaData>>(
        `/desks/mesa-midia/${deskId}/acompanhamento`,
        { params },
      );
    } catch (error) {
      return getErrorResponse<ApiResponse<MesaMidiaData>>(error);
    }
  },
  updateBase: async (
    deskId: number | string,
    payload: MesaMidiaBasePayload,
  ): Promise<AxiosResponse<ApiResponse<MesaMidiaConfig>> | undefined> => {
    try {
      return await api.patch<ApiResponse<MesaMidiaConfig>>(
        `/desks/mesa-midia/${deskId}/acompanhamento/base`,
        payload,
      );
    } catch (error) {
      return getErrorResponse<ApiResponse<MesaMidiaConfig>>(error);
    }
  },
  saveDiario: async (
    deskId: number | string,
    payload: MesaMidiaDiarioPayload,
  ): Promise<AxiosResponse<ApiResponse<MesaMidiaRegistro>> | undefined> => {
    try {
      return await api.post<ApiResponse<MesaMidiaRegistro>>(
        `/desks/mesa-midia/${deskId}/acompanhamento/diario`,
        payload,
      );
    } catch (error) {
      return getErrorResponse<ApiResponse<MesaMidiaRegistro>>(error);
    }
  },
  deleteDiario: async (
    deskId: number | string,
    registroId: number | string,
  ): Promise<AxiosResponse<{ status: boolean; msg?: string }> | undefined> => {
    try {
      return await api.delete<{ status: boolean; msg?: string }>(
        `/desks/mesa-midia/${deskId}/acompanhamento/diario/${registroId}`,
      );
    } catch (error) {
      return getErrorResponse<{ status: boolean; msg?: string }>(error);
    }
  },
};
